import type { OptimizelyDataFile } from './optimizely.types';
import { logError } from '@zoopla/logger';
import fetch from 'isomorphic-unfetch';
import AbortController from 'abort-controller';
import cache from 'memory-cache';

const populateCache = async (): Promise<OptimizelyDataFile | undefined> => {
    if (!process.env['NEXT_PUBLIC_OPTIMIZELY_URL']) {
        logError('Missing NEXT_PUBLIC_OPTIMIZELY_URL environment variable');
        return;
    }

    if (!process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']) {
        logError('Missing NEXT_PUBLIC_OPTIMIZELY_KEY environment variable');
        return;
    }

    const OPTIMIZELY_KEY = process.env['NEXT_PUBLIC_OPTIMIZELY_KEY'];

    try {
        const controller = new AbortController();
        const dataFileTimeout = setTimeout(() => {
            controller.abort();
            logError('Timeout retrieving dataFile');
        }, 1000);

        const optimizelyResponse = await fetch(
            `${process.env['NEXT_PUBLIC_OPTIMIZELY_URL']}/datafiles/${OPTIMIZELY_KEY}.json`,
            {
                signal: controller.signal
            }
        );

        clearTimeout(dataFileTimeout);

        if (optimizelyResponse.status !== 200) {
            throw new Error(
                `Bad response from server: ${optimizelyResponse.status}`
            );
        }

        const data = await optimizelyResponse.json();
        const fiveMinutes = 300000;

        cache.put(OPTIMIZELY_KEY, data, fiveMinutes);

        return data;
    } catch (e) {
        logError('failed to get dataFile', {
            error: e
        });
        return;
    }
};

export const getDataFile = async (): Promise<
    OptimizelyDataFile | undefined
> => {
    if (!process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']) {
        logError('Missing NEXT_PUBLIC_OPTIMIZELY_KEY environment variable');
        return;
    }

    const cachedResponse = cache.get(process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']);

    if (cachedResponse) {
        return cachedResponse;
    }

    return populateCache();
};

export { cache };
